// Outbox of changes made without a connection: queue, merge, ordered replay, outcome per item.
// Pure logic without browser APIs or imports, so node --test can run it with an in-memory store
// (test/offline/outbox.test.js). The browser wiring (IndexedDB, axios, events) is in ./index.js.
//
// An item is one HTTP request:
//   { id            idempotency key, sent as the Idempotency-Key header on every attempt
//     seq           replay order
//     owner         "<tenant>|<userId>": only the signed-in user's items are replayed
//     module, kind  e.g. "work-reports", "create" | "update" | "photo" | "clock"
//     entity        "<module>:<record id>" — items of one entity replay strictly in order, and a
//                   failed item holds back the later ones of its entity (never other entities)
//     target        record id in the URL ("{id}"), possibly a local id of a queued create
//     localId       for a create: the local id the record is known by until it is synced
//     method, url, body, files, baseVersion (updatedAt the edit was made against)
//     idPath, versionPath   where the response carries the new record's id / updatedAt
//     state         pending | error | conflict | auth
//     attempted     sent at least once: it may have reached the server, so its body is frozen
//     attempts, nextAttemptAt, error, label, createdAt, updatedAt }
//
// Outcomes: 2xx removes the item (a create maps its local id to the server id and relinks the
// later items of that record); no response (offline) stops the run and retries later with
// backoff; 5xx/408/429 retry with backoff; 401 pauses everything until the user signs in again;
// 409 is a conflict (record changed or locked on the server) and other 4xx an error — both
// wait for the user (retry, overwrite or discard) instead of being dropped or overwritten.

export const STATES = Object.freeze({
  PENDING: "pending",
  ERROR: "error",
  CONFLICT: "conflict",
  AUTH: "auth"
});

export const LOCAL_PREFIX = "local-";
export const isLocalId = id => typeof id === "string" && id.startsWith(LOCAL_PREFIX);

const RETRY_DELAYS = [5000, 15000, 30000, 60000, 120000, 300000];
const BUSY_DELAY = 2000;

const pathGet = (object, path) =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => (value == null ? undefined : value[key]), object);

const randomPart = () => Math.random().toString(36).slice(2, 10);

export const newKey = (now = Date.now()) => `ob-${now.toString(36)}-${randomPart()}${randomPart()}`;
export const newLocalId = (now = Date.now()) => `${LOCAL_PREFIX}${now.toString(36)}${randomPart()}`;

export const entityKey = (module, id) => `${module}:${id}`;

const bySeq = (a, b) => a.seq - b.seq;

/**
 * store: async all(), put(item), remove(id), getId(localId), putId(localId, id),
 *        removeBlob(blobId) (optional)
 * transport: async send(item) → { status, data }; throws when there is no response at all
 * owner: () => current owner key (null when nobody is signed in: nothing is replayed)
 */
export function createOutbox({
  store,
  transport,
  owner,
  now = () => Date.now(),
  retryDelays = RETRY_DELAYS
}) {
  const listeners = new Set();
  const syncedListeners = new Set();
  let running = null;
  let again = false;

  const notify = () => listeners.forEach(listener => listener());
  const subscribe = listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  const onSynced = listener => {
    syncedListeners.add(listener);
    return () => syncedListeners.delete(listener);
  };

  const all = async () => (await store.all()).slice().sort(bySeq);
  const mine = async () => {
    const who = owner();
    return who ? (await all()).filter(item => item.owner === who) : [];
  };

  const enqueue = async fields => {
    const items = await all();
    const time = now();
    const item = {
      attempted: false,
      attempts: 0,
      nextAttemptAt: 0,
      error: null,
      files: [],
      ...fields,
      id: fields.id || newKey(time),
      seq: items.reduce((max, other) => Math.max(max, other.seq), 0) + 1,
      owner: fields.owner || owner(),
      entity: fields.entity || entityKey(fields.module, fields.localId || fields.target),
      state: STATES.PENDING,
      createdAt: time,
      updatedAt: time
    };
    await store.put(item);
    notify();
    return item;
  };

  /**
   * A further edit of a record that still waits in the outbox replaces the body of the waiting
   * create/update when that was never sent; returns the merged item, or null when the edit has
   * to be queued as an item of its own.
   */
  const mergeIntoPending = async (entity, body) => {
    const items = (await mine()).filter(item => item.entity === entity);
    const last = items[items.length - 1];
    if (
      !last ||
      last.attempted ||
      last.state !== STATES.PENDING ||
      !["create", "update"].includes(last.kind)
    ) {
      return null;
    }
    const merged = { ...last, body: { ...last.body, ...body }, updatedAt: now() };
    await store.put(merged);
    notify();
    return merged;
  };

  const removeItems = async items => {
    for (const item of items) {
      await store.remove(item.id);
      for (const file of item.files || []) {
        if (file.blobId && store.removeBlob) await store.removeBlob(file.blobId);
      }
    }
  };

  /** Drops an item; a create takes the queued changes of its (never created) record along. */
  const discard = async id => {
    const items = await all();
    const item = items.find(other => other.id === id);
    if (!item) return [];
    const gone =
      item.kind === "create" ? items.filter(other => other.entity === item.entity) : [item];
    await removeItems(gone);
    notify();
    return gone;
  };

  /** Back into the queue; `overwrite` drops the version check of an update in conflict. */
  const retry = async (id, { overwrite = false } = {}) => {
    const item = (await all()).find(other => other.id === id);
    if (!item) return null;
    const next = {
      ...item,
      state: STATES.PENDING,
      nextAttemptAt: 0,
      error: null,
      updatedAt: now(),
      ...(overwrite ? { baseVersion: null } : {})
    };
    await store.put(next);
    notify();
    return next;
  };

  const resolveId = async id => (isLocalId(id) ? (await store.getId(id)) || id : id);

  const delayFor = attempts => retryDelays[Math.min(attempts, retryDelays.length) - 1] || 0;

  // After a create succeeded its record has a server id: the later items of the record now
  // address it directly (and survive the id map being lost).
  const relink = async (item, serverId, version) => {
    const newEntity = entityKey(item.module, serverId);
    for (const other of await all()) {
      if (other.entity !== item.entity || other.id === item.id) continue;
      await store.put({
        ...other,
        entity: newEntity,
        target: other.target === item.localId ? serverId : other.target,
        ...(version && other.kind === "update" && other.baseVersion !== null
          ? { baseVersion: version }
          : {})
      });
    }
  };

  // Our own successful change moved the record's version: later queued updates of it were
  // made on top of that change, so they are checked against the new version.
  const advanceVersion = async (entity, sentId, version) => {
    for (const other of await all()) {
      if (
        other.entity === entity &&
        other.id !== sentId &&
        other.kind === "update" &&
        other.baseVersion !== null
      ) {
        await store.put({ ...other, baseVersion: version });
      }
    }
  };

  const settle = async (item, patch) => {
    await store.put({ ...item, ...patch, updatedAt: now() });
  };

  const runOnce = async ({ force }) => {
    const result = { sent: 0, failed: 0, offline: false, auth: false };
    const held = new Set();
    for (const listed of await mine()) {
      if (held.has(listed.entity)) continue;
      // Re-read: an earlier item of this run may have relinked it
      const item = (await all()).find(other => other.id === listed.id);
      if (!item) continue;
      if (item.state === STATES.ERROR || item.state === STATES.CONFLICT) {
        held.add(item.entity);
        continue;
      }
      if (!force && item.nextAttemptAt > now()) {
        held.add(item.entity);
        continue;
      }
      const target = await resolveId(item.target);
      if (isLocalId(target)) {
        // Its create has not gone through yet
        held.add(item.entity);
        continue;
      }
      const sending = {
        ...item,
        target,
        url: String(item.url).replace("{id}", encodeURIComponent(target || "")),
        attempted: true,
        attempts: item.attempts + 1
      };
      // Frozen before it leaves: it may arrive even if we never hear back
      await settle(item, { attempted: true, attempts: sending.attempts });
      let response;
      try {
        response = await transport.send(sending);
      } catch (error) {
        await settle(sending, {
          state: STATES.PENDING,
          nextAttemptAt: now() + delayFor(sending.attempts),
          error: { network: true, message: (error && error.message) || "No connection" }
        });
        result.offline = true;
        break;
      }
      const { status, data } = response;
      const code = data && data.code;
      if (status >= 200 && status < 300) {
        const version = pathGet(data, item.versionPath);
        if (item.kind === "create" && item.localId) {
          const serverId = pathGet(data, item.idPath);
          if (serverId) {
            await store.putId(item.localId, String(serverId));
            await relink(item, String(serverId), version);
          }
        } else if (version) {
          await advanceVersion(item.entity, item.id, version);
        }
        await removeItems([item]);
        result.sent += 1;
        syncedListeners.forEach(listener => listener(sending, response));
        continue;
      }
      held.add(item.entity);
      result.failed += 1;
      const error = {
        status,
        code: code || null,
        message: (data && (data.errorMessage || data.message || data.error)) || ""
      };
      if (status === 401) {
        await settle(sending, { state: STATES.AUTH, error });
        result.auth = true;
        break;
      }
      if (status === 409 && code === "IDEMPOTENCY_IN_PROGRESS") {
        await settle(sending, { state: STATES.PENDING, nextAttemptAt: now() + BUSY_DELAY, error });
      } else if (status >= 500 || status === 408 || status === 429) {
        await settle(sending, {
          state: STATES.PENDING,
          nextAttemptAt: now() + delayFor(sending.attempts),
          error
        });
      } else {
        await settle(sending, {
          state: status === 409 ? STATES.CONFLICT : STATES.ERROR,
          error
        });
      }
    }
    return result;
  };

  /**
   * Replays the signed-in user's items in order. One run at a time; a call during a run makes
   * the run go once more (an item queued meanwhile is not left behind).
   */
  const flush = async ({ force = false } = {}) => {
    if (running) {
      again = true;
      return running;
    }
    running = (async () => {
      let total = { sent: 0, failed: 0, offline: false, auth: false };
      try {
        do {
          again = false;
          const result = await runOnce({ force });
          total = {
            sent: total.sent + result.sent,
            failed: total.failed + result.failed,
            offline: result.offline,
            auth: result.auth
          };
          if (result.offline || result.auth) break;
        } while (again);
      } finally {
        running = null;
        notify();
      }
      return total;
    })();
    return running;
  };

  return {
    enqueue,
    mergeIntoPending,
    discard,
    retry,
    flush,
    resolveId,
    list: mine,
    all,
    subscribe,
    onSynced,
    isRunning: () => Boolean(running)
  };
}

/** Same interface as the IndexedDB store, in memory: tests and browsers without IndexedDB. */
export function createMemoryStore() {
  const items = new Map();
  const ids = new Map();
  const blobs = new Map();
  const cache = new Map();
  const copy = value => (value === undefined ? value : JSON.parse(JSON.stringify(value)));
  return {
    all: async () => [...items.values()].map(copy),
    put: async item => void items.set(item.id, copy(item)),
    remove: async id => void items.delete(id),
    getId: async localId => ids.get(localId) || null,
    putId: async (localId, id) => void ids.set(localId, id),
    putBlob: async (id, value) => void blobs.set(id, value),
    getBlob: async id => blobs.get(id) || null,
    removeBlob: async id => void blobs.delete(id),
    getCache: async key => cache.get(key) || null,
    putCache: async (key, value) => void cache.set(key, value),
    clearCache: async () => cache.clear()
  };
}
